import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Calculator, ClipboardCheck, Layers, Package, Lightbulb, Award, Users, Clock, Shield, Handshake, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const companyInfo = {
  name: "D&J Engineering",
  logo: "https://customer-assets.emergentagent.com/job_bb6c1548-0053-4d1d-9174-d34b5653b3e9/artifacts/43j18h5f_image.png",
  phone: "(819) 775-2130",
  phone2: "(647) 701-4224",
};

const images = {
  hero: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80",
  construction: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80",
  blueprints: "https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=800&q=80",
  bim: "https://images.unsplash.com/photo-1612888262725-6b300edf916c?w=800&q=80",
  team: "https://images.unsplash.com/photo-1580982330720-bd5e0fed108b?w=800&q=80",
  estimation: "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800&q=80",
  management: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800&q=80",
};

const serviceImages = {
  structural: "https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=800&q=80",
  estimation: "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800&q=80",
  management: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80",
  bim: "https://images.unsplash.com/photo-1612888262725-6b300edf916c?w=800&q=80",
  allinone: "https://images.unsplash.com/photo-1580982330720-bd5e0fed108b?w=800&q=80",
};

const iconMap = {
  Building2,
  Calculator,
  ClipboardCheck,
  Layers,
  Package,
  Lightbulb,
  Award,
  Users,
  Clock,
  Shield,
  Handshake,
};

const HomePage = () => {
  const { t } = useLanguage();

  const services = [
    { id: 1, key: 'structural', icon: "Building2" },
    { id: 2, key: 'estimation', icon: "Calculator" },
    { id: 3, key: 'management', icon: "ClipboardCheck" },
    { id: 4, key: 'bim', icon: "Layers" },
    { id: 5, key: 'allinone', icon: "Package" },
  ].map(s => ({
    ...s,
    title: t(`services.items.${s.key}.title`),
    shortDesc: t(`services.items.${s.key}.shortDesc`),
    fullDesc: t(`services.items.${s.key}.fullDesc`),
    features: t(`services.items.${s.key}.features`),
    image: serviceImages[s.key],
  }));

  const whyChooseUs = [
    { id: 1, icon: "Lightbulb", ...t('whyUs.items.expertise') },
    { id: 2, icon: "Award", ...t('whyUs.items.quality') },
    { id: 3, icon: "Users", ...t('whyUs.items.support') },
    { id: 4, icon: "Clock", ...t('whyUs.items.deadline') },
    { id: 5, icon: "Shield", ...t('whyUs.items.professionalism') },
    { id: 6, icon: "Handshake", ...t('whyUs.items.trust') },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={images.hero} 
            alt="Construction" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 mb-8">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-sm font-medium text-blue-300">{t('hero.badge')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.title')}
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10">
              {t('hero.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-6 text-lg rounded-xl font-medium transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 group">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#services">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl font-medium transition-all duration-300">
                  {t('hero.secondaryCta')}
                </Button>
              </a>
            </div>
          </div>
        </div>
        
        {/* Floating Image Cards */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 hidden xl:block">
          <div className="relative">
            <div className="w-64 h-40 rounded-2xl overflow-hidden shadow-2xl rotate-3 translate-x-8">
              <img src={images.blueprints} alt="Blueprints" className="w-full h-full object-cover" />
            </div>
            <div className="w-64 h-40 rounded-2xl overflow-hidden shadow-2xl -rotate-3 -translate-y-8">
              <img src={images.bim} alt="BIM Modeling" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-blue-700 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "15+", label: t('about.stats.experience') },
              { number: "200+", label: t('about.stats.projects') },
              { number: "100%", label: t('about.stats.satisfaction') },
              { number: "OIQ et OIO", label: t('about.stats.members') },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-blue-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Brief Section with Images */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              {/* Image Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="h-48 rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.construction} alt="Construction" className="w-full h-full object-cover" />
                  </div>
                  <div className="h-32 rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.blueprints} alt="Blueprints" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="h-32 rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.bim} alt="BIM" className="w-full h-full object-cover" />
                  </div>
                  <div className="h-48 rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.team} alt="Team" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-blue-700 text-white px-6 py-4 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">15+</div>
                <div className="text-blue-100 text-sm">{t('about.stats.experience')}</div>
              </div>
            </div>
            
            <div>
              <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
                {t('about.sectionTitle')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                {t('about.title')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('about.description')}
              </p>
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-8">
                <div className="w-12 h-12 rounded-full bg-blue-700 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <p className="text-blue-900 font-medium">{t('about.highlight')}</p>
              </div>
              <Link to="/about">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg group">
                  {t('aboutPage.meetTeam')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Full Detail */}
      <section id="services" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
              {t('services.sectionTitle')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('services.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>
          
          {/* Services Detail */}
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building2;
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={service.id} 
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-700/30">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <span className="text-blue-700 font-semibold text-sm">Service {String(index + 1).padStart(2, '0')}</span>
                        <h3 className="text-2xl md:text-3xl font-bold text-slate-900">{service.title}</h3>
                      </div>
                    </div>
                    
                    <p className="text-lg text-slate-600 leading-relaxed mb-8">
                      {service.fullDesc}
                    </p>
                    
                    <ul className="space-y-4">
                      {service.features && Array.isArray(service.features) && service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <Card className="bg-white border-0 shadow-2xl shadow-slate-200/50 overflow-hidden group">
                      <CardContent className="p-0">
                        <div className="h-80 relative overflow-hidden">
                          <img 
                            src={service.image} 
                            alt={service.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
              {t('whyUs.sectionTitle')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('whyUs.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('whyUs.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon] || Shield;
              return (
                <div key={item.id} className="group p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-xl border border-transparent hover:border-slate-100 transition-all duration-300">
                  <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-blue-500 group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-blue-700 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
              {t('services.sectionTitle')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('services.process.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('services.process.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {t('services.process.steps') && t('services.process.steps').map((item, idx) => (
              <div key={idx} className="relative text-center group">
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent" />
                )}
                <div className="relative z-10 w-16 h-16 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500 group-hover:border-blue-500 transition-colors duration-300 shadow-lg">
                  <span className="text-xl font-bold text-blue-700 group-hover:text-white transition-colors duration-300">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={images.construction} 
            alt="Construction" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/90" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-blue-500 hover:bg-blue-400 text-white px-10 py-6 text-lg rounded-xl font-medium transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 group">
                {t('cta.button')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 text-lg rounded-xl font-medium transition-all duration-300">
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
